/**
 * Availability Calendar Component
 * Monthly calendar view showing driver availability
 */

import { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { WeeklyTimeSlot, ScheduleException, DayOfWeek } from '../../types';

interface AvailabilityCalendarProps {
  weeklySchedule: WeeklyTimeSlot[];
  exceptions: ScheduleException[];
  onDateClick?: (date: Date) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MAP: Record<number, DayOfWeek> = {
  0: 'sun',
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
};

export default function AvailabilityCalendar({
  weeklySchedule,
  exceptions,
  onDateClick,
}: AvailabilityCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    
    // Add days from previous month to fill the first week
    const startPadding = firstDay.getDay();
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add days from next month to complete the last week
    const endPadding = 6 - lastDay.getDay();
    for (let i = 1; i <= endPadding; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [currentMonth]);

  // Check if a date is available based on weekly schedule and exceptions
  const getAvailability = (date: Date): { available: boolean; hours?: string; exception?: ScheduleException } => {
    const dateStr = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Baghdad' });
    
    // Check for exception first
    const exception = exceptions.find((e) => e.date === dateStr);
    if (exception) {
      if (exception.start && exception.end) {
        return { available: true, hours: `${exception.start}-${exception.end}`, exception };
      }
      return { available: false, exception };
    }
    
    // Check weekly schedule
    const dayOfWeek = DAY_MAP[date.getDay()];
    const slot = weeklySchedule.find((s) => s.day === dayOfWeek && s.is_available);
    
    if (slot) {
      return { available: true, hours: `${slot.start}-${slot.end}` };
    }
    
    return { available: false };
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const monthYearLabel = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Availability Calendar</CardTitle>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="ghost" size="sm" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">{monthYearLabel}</p>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map(({ date, isCurrentMonth }, index) => {
            const { available, hours, exception } = getAvailability(date);
            const today = isToday(date);

            return (
              <button
                key={index}
                onClick={() => onDateClick?.(date)}
                className={cn(
                  'aspect-square p-1 rounded-lg text-sm transition-colors relative',
                  'hover:bg-gray-100',
                  !isCurrentMonth && 'opacity-40',
                  today && 'ring-2 ring-blue-500',
                  available
                    ? 'bg-green-50 text-green-900'
                    : 'bg-gray-50 text-gray-500',
                  exception && !exception.start && 'bg-red-50 text-red-600'
                )}
              >
                <span className="font-medium">{date.getDate()}</span>
                {isCurrentMonth && hours && (
                  <span className="absolute bottom-0.5 left-0 right-0 text-[9px] text-center truncate px-0.5">
                    {hours}
                  </span>
                )}
                {exception && (
                  <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-orange-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
            <span className="text-gray-600">Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
            <span className="text-gray-600">Off</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-red-100 border border-red-300" />
            <span className="text-gray-600">Exception</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
